import { z } from "zod";

/**
 * Cookie-consent contract shared by the API and all three frontends.
 *
 * One category is negotiable: analytics and advertising. Login, wallet, routing
 * and UI preference storage are essential and load without asking. The privacy
 * policy must describe the preference cookies rather than claim they are exempt.
 */

/** Bump when the policy text or the vendor list changes; forces a re-prompt. */
export const CONSENT_POLICY_VERSION = 1;

export const CONSENT_COOKIE_NAME = "cede_consent";

/** CNIL's guidance is that a choice should not stand indefinitely. */
export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

/**
 * Shorter than a real decision: a visitor outside the EU was never asked, and a
 * month later they may be somewhere that requires asking.
 */
export const CONSENT_REGION_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/** Which surface recorded the decision. */
export const CONSENT_APPS = ["arena", "arena-lp", "hub"] as const;
export type ConsentApp = (typeof CONSENT_APPS)[number];

/**
 * `user` means they clicked. `region` means we resolved them outside the EU and
 * never showed a banner, which is a materially different claim in an audit.
 */
export const CONSENT_BASES = ["user", "region"] as const;
export type ConsentBasis = (typeof CONSENT_BASES)[number];

export const ConsentCookieSchema = z.object({
  /** Analytics and advertising. The only category the visitor decides. */
  analytics: z.boolean(),
  basis: z.enum(CONSENT_BASES),
  /** Policy version in force when the choice was made. */
  pv: z.number().int().nonnegative(),
  /** Client-generated join key to the server-side audit row. Never a user id. */
  id: z.string().uuid(),
  /** False until the audit POST lands, so a later page load can retry it. */
  synced: z.boolean(),
});
export type ConsentCookie = z.infer<typeof ConsentCookieSchema>;

/**
 * Countries where a non-essential tag needs consent before it loads.
 *
 * EU 27 plus the EEA three plus the UK, and the EU territories that carry their
 * own ISO code rather than their member state's: Aland, and the French outermost
 * regions. The edge reports those codes, so leaving them out would read a French
 * or Finnish visitor as outside the EU. Madeira, the Azores and the Canaries need
 * no entry; they report as PT and ES.
 *
 * The crown dependencies run their own near-identical regimes and are cheap to
 * include, so they are.
 */
export const CONSENT_REQUIRED_COUNTRIES: ReadonlySet<string> = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "IS",
  "LI",
  "NO",
  "GB",
  "GG",
  "JE",
  "IM",
  "AX",
  "GF",
  "GP",
  "MQ",
  "YT",
  "RE",
  "MF",
]);

/**
 * Whether this visitor must be asked. Only a country we resolved into the list
 * is: asking is not free, because the ask path clears `_ga`/`_gcl`/`_fbp`/`mp_`
 * before the banner renders, so an unresolvable country charges a real
 * acquisition identity for a guess — and a refusal then stands for 180 days.
 *
 * Deliberately the opposite direction to `decideArenaGeoAccess`, which still
 * fails closed on a missing country. That one withholds a market from someone
 * who may not enter it; this one withholds a measurement. The wrong answer does
 * not cost the same on both sides.
 */
export const consentRequiredForCountry = (countryCode: string | null): boolean => {
  if (countryCode === null) return false;
  return CONSENT_REQUIRED_COUNTRIES.has(countryCode);
};

/** A choice is stale once the policy it was given against is superseded. */
export const isConsentCurrent = (consent: ConsentCookie): boolean => consent.pv === CONSENT_POLICY_VERSION;
